import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Onboarding, UpdateOnboardingLinksInput } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { SelectField, TextField, type SelectOption } from '../../components/ui/Field.js';
import { useAgreements } from '../agreements/api.js';
import { useOpportunities } from '../opportunities/api.js';
import { useProjects } from '../projects/api.js';
import { useTickets } from '../tickets/api.js';
import { useUpdateOnboardingLinks } from './api.js';

interface OnboardingLinksTabProps {
  onboarding: Onboarding;
  canEdit: boolean;
}

/** Enough rows to cover every record a single customer realistically has. */
const PICKER_PAGE_SIZE = 200;

interface LinkRowProps {
  title: string;
  description: string;
  current: JSX.Element | string;
  isLinked: boolean;
  onOpen: (() => void) | null;
  children?: JSX.Element | null;
}

function LinkRow({
  title,
  description,
  current,
  isLinked,
  onOpen,
  children,
}: LinkRowProps): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-slate-800">{title}</div>
          <p className="mt-0.5 text-xs text-slate-500">{description}</p>
        </div>
        {isLinked && onOpen ? (
          <Button variant="secondary" size="sm" onClick={onOpen}>
            Go to {title.toLowerCase()}
          </Button>
        ) : null}
      </div>
      <div className="mt-3 text-sm text-slate-900">{current}</div>
      {children ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

function NotLinked(): JSX.Element {
  return <span className="text-sm text-slate-400">Not linked</span>;
}

export function OnboardingLinksTab({
  onboarding,
  canEdit,
}: OnboardingLinksTabProps): JSX.Element {
  const navigate = useNavigate();
  const updateLinks = useUpdateOnboardingLinks();

  const [opportunityId, setOpportunityId] = useState(onboarding.opportunityId);
  const [projectId, setProjectId] = useState(onboarding.projectId ?? '');
  const [ticketId, setTicketId] = useState(onboarding.ticketId ?? '');
  const [agreementId, setAgreementId] = useState(onboarding.agreementId ?? '');
  const [cuListId, setCuListId] = useState(onboarding.cuListId ?? '');
  const [cuListName, setCuListName] = useState(onboarding.cuListName ?? '');
  const [cuListUrl, setCuListUrl] = useState(onboarding.cuListUrl ?? '');

  // Re-seed the editors whenever the server record changes (e.g. after a save).
  useEffect(() => {
    setOpportunityId(onboarding.opportunityId);
    setProjectId(onboarding.projectId ?? '');
    setTicketId(onboarding.ticketId ?? '');
    setAgreementId(onboarding.agreementId ?? '');
    setCuListId(onboarding.cuListId ?? '');
    setCuListName(onboarding.cuListName ?? '');
    setCuListUrl(onboarding.cuListUrl ?? '');
  }, [
    onboarding.opportunityId,
    onboarding.projectId,
    onboarding.ticketId,
    onboarding.agreementId,
    onboarding.cuListId,
    onboarding.cuListName,
    onboarding.cuListUrl,
  ]);

  const opportunitiesQuery = useOpportunities(
    { page: 1, pageSize: PICKER_PAGE_SIZE, customerId: onboarding.customerId },
    canEdit,
  );
  const projectsQuery = useProjects({
    page: 1,
    pageSize: PICKER_PAGE_SIZE,
    customerId: onboarding.customerId,
  });
  const ticketsQuery = useTickets({
    page: 1,
    pageSize: PICKER_PAGE_SIZE,
    customerId: onboarding.customerId,
  });
  const agreementsQuery = useAgreements(
    { page: 1, pageSize: PICKER_PAGE_SIZE, customerId: onboarding.customerId },
    { enabled: canEdit },
  );

  const opportunityOptions: SelectOption[] = (opportunitiesQuery.data?.data ?? []).map((o) => ({
    value: o.id,
    label: o.name,
  }));
  const projectOptions: SelectOption[] = (projectsQuery.data?.data ?? []).map((p) => ({
    value: p.id,
    label: `#${p.number} — ${p.name}`,
  }));
  const ticketOptions: SelectOption[] = (ticketsQuery.data?.data ?? []).map((t) => ({
    value: t.id,
    label: `${t.referenceId ?? `#${t.number}`} — ${t.title}`,
  }));
  const agreementOptions: SelectOption[] = (agreementsQuery.data?.data ?? []).map((a) => ({
    value: a.id,
    label: `#${a.number} — ${a.name}`,
  }));

  const save = (input: UpdateOnboardingLinksInput): void => {
    updateLinks.mutate({ id: onboarding.id, input });
  };

  const trimmedCuListId = cuListId.trim();
  const cuDirty =
    trimmedCuListId !== (onboarding.cuListId ?? '') ||
    cuListName.trim() !== (onboarding.cuListName ?? '') ||
    cuListUrl.trim() !== (onboarding.cuListUrl ?? '');

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Link the records this onboarding belongs to. Use this when a link is missing — for
        example a project or agreement that was created outside the onboarding steps.
      </p>

      <LinkRow
        title="Opportunity"
        description="The source opportunity. Repointing also moves this onboarding to that opportunity's customer."
        isLinked
        onOpen={() =>
          navigate(`/sales/opportunities/${onboarding.opportunityId}?returnOnboardingId=${onboarding.id}`)
        }
        current={
          <span className="font-medium">
            {onboarding.opportunityName}
            <span className="ml-2 font-normal text-slate-500">({onboarding.customerName})</span>
          </span>
        }
      >
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[16rem] flex-1">
              <SelectField
                label="Linked opportunity"
                value={opportunityId}
                options={opportunityOptions}
                placeholder="Select an opportunity"
                onChange={(e) => setOpportunityId(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              isLoading={updateLinks.isPending}
              disabled={!opportunityId || opportunityId === onboarding.opportunityId}
              onClick={() => save({ opportunityId })}
            >
              Save
            </Button>
          </div>
        ) : null}
      </LinkRow>

      <LinkRow
        title="Project"
        description="The PSA project delivering this onboarding. Linking also stamps the project with this opportunity."
        isLinked={Boolean(onboarding.projectId)}
        onOpen={() =>
          navigate(`/projects/${onboarding.projectId ?? ''}?returnOnboardingId=${onboarding.id}`)
        }
        current={
          onboarding.projectId ? (
            <span className="font-medium">
              {onboarding.projectNumber != null ? `#${onboarding.projectNumber} — ` : ''}
              {onboarding.projectName ?? onboarding.projectId}
            </span>
          ) : (
            <NotLinked />
          )
        }
      >
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[16rem] flex-1">
              <SelectField
                label="Linked project"
                value={projectId}
                options={[{ value: '', label: '— Not linked —' }, ...projectOptions]}
                onChange={(e) => setProjectId(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              isLoading={updateLinks.isPending}
              disabled={projectId === (onboarding.projectId ?? '')}
              onClick={() => save({ projectId: projectId || null })}
            >
              Save
            </Button>
          </div>
        ) : null}
      </LinkRow>

      <LinkRow
        title="Ticket"
        description="The PSA ticket delivering this onboarding, when the work is tracked on a ticket instead of a project."
        isLinked={Boolean(onboarding.ticketId)}
        onOpen={() => navigate(`/tickets/${onboarding.ticketId ?? ''}`)}
        current={
          onboarding.ticketId ? (
            <span className="font-medium">
              {onboarding.ticketReferenceId ??
                (onboarding.ticketNumber != null ? `#${onboarding.ticketNumber}` : '')}
              {onboarding.ticketTitle ? ` — ${onboarding.ticketTitle}` : ''}
            </span>
          ) : (
            <NotLinked />
          )
        }
      >
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[16rem] flex-1">
              <SelectField
                label="Linked ticket"
                value={ticketId}
                options={[{ value: '', label: '— Not linked —' }, ...ticketOptions]}
                onChange={(e) => setTicketId(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              isLoading={updateLinks.isPending}
              disabled={ticketId === (onboarding.ticketId ?? '')}
              onClick={() => save({ ticketId: ticketId || null })}
            >
              Save
            </Button>
          </div>
        ) : null}
      </LinkRow>

      <LinkRow
        title="Agreement"
        description="The PSA agreement created for this customer as part of onboarding."
        isLinked={Boolean(onboarding.agreementId)}
        onOpen={() => navigate(`/agreements/${onboarding.agreementId ?? ''}`)}
        current={
          onboarding.agreementId ? (
            <span className="font-medium">{onboarding.agreementName ?? onboarding.agreementId}</span>
          ) : (
            <NotLinked />
          )
        }
      >
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[16rem] flex-1">
              <SelectField
                label="Linked agreement"
                value={agreementId}
                options={[{ value: '', label: '— Not linked —' }, ...agreementOptions]}
                onChange={(e) => setAgreementId(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              isLoading={updateLinks.isPending}
              disabled={agreementId === (onboarding.agreementId ?? '')}
              onClick={() => save({ agreementId: agreementId || null })}
            >
              Save
            </Button>
          </div>
        ) : null}
      </LinkRow>

      <LinkRow
        title="CU project"
        description="The ClickUp list that represents this project. Paste the list id from ClickUp; the deep link is filled in automatically when left blank."
        isLinked={Boolean(onboarding.cuListId && onboarding.cuListUrl)}
        onOpen={() =>
          window.open(onboarding.cuListUrl ?? '', '_blank', 'noopener,noreferrer')
        }
        current={
          onboarding.cuListId ? (
            <span className="font-medium">
              {onboarding.cuListUrl ? (
                <a
                  href={onboarding.cuListUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline"
                >
                  {onboarding.cuListName ?? onboarding.cuListId}
                </a>
              ) : (
                (onboarding.cuListName ?? onboarding.cuListId)
              )}
              <span className="ml-2 font-normal text-slate-500">{onboarding.cuListId}</span>
            </span>
          ) : (
            <NotLinked />
          )
        }
      >
        {canEdit ? (
          <div className="space-y-2">
            <div className="grid gap-2 sm:grid-cols-3">
              <TextField
                label="ClickUp list id"
                value={cuListId}
                placeholder="901234567890"
                onChange={(e) => setCuListId(e.target.value)}
              />
              <TextField
                label="List name"
                value={cuListName}
                placeholder="Customer - Project"
                onChange={(e) => setCuListName(e.target.value)}
              />
              <TextField
                label="List URL"
                value={cuListUrl}
                placeholder="https://app.clickup.com/…"
                onChange={(e) => setCuListUrl(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                isLoading={updateLinks.isPending}
                disabled={!cuDirty}
                onClick={() =>
                  save({
                    cuListId: trimmedCuListId || null,
                    cuListName: cuListName.trim() || null,
                    cuListUrl: cuListUrl.trim() || null,
                  })
                }
              >
                Save
              </Button>
              {onboarding.cuListId ? (
                <Button
                  variant="ghost"
                  className="text-rose-600 hover:bg-rose-50"
                  isLoading={updateLinks.isPending}
                  onClick={() => save({ cuListId: null })}
                >
                  Unlink
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </LinkRow>

      {updateLinks.isError ? (
        <p className="text-sm text-rose-600">
          {updateLinks.error instanceof Error ? updateLinks.error.message : 'Could not save links.'}
        </p>
      ) : null}
    </div>
  );
}
